import React from "react";
import { motion } from "framer-motion";
import {
  Compass,
  Bell,
  Building2,
  Car,
  Heart,
  Award,
  ShieldCheck,
  Sparkles,
  CalendarHeart,
  HandHeart,
} from "lucide-react";
import { useLang } from "../lib/i18n";

const buildRoadmap = (t) => [
  { icon: <Compass className="w-4 h-4" />, title: t("roadmap.item1.title"), desc: t("roadmap.item1.desc"), status: "live" },
  { icon: <Bell className="w-4 h-4" />, title: t("roadmap.item2.title"), desc: t("roadmap.item2.desc"), status: "live" },
  { icon: <Building2 className="w-4 h-4" />, title: t("roadmap.item3.title"), desc: t("roadmap.item3.desc"), status: "soon" },
  { icon: <Car className="w-4 h-4" />, title: t("roadmap.item4.title"), desc: t("roadmap.item4.desc"), status: "live" },
  { icon: <Heart className="w-4 h-4" />, title: t("roadmap.item5.title"), desc: t("roadmap.item5.desc"), status: "soon" },
  { icon: <Award className="w-4 h-4" />, title: t("roadmap.item6.title"), desc: t("roadmap.item6.desc"), status: "soon" },
  { icon: <ShieldCheck className="w-4 h-4" />, title: t("roadmap.item7.title"), desc: t("roadmap.item7.desc"), status: "live" },
  { icon: <CalendarHeart className="w-4 h-4" />, title: t("roadmap.item8.title"), desc: t("roadmap.item8.desc"), status: "soon" },
  { icon: <HandHeart className="w-4 h-4" />, title: t("roadmap.item9.title"), desc: t("roadmap.item9.desc"), status: "soon" },
  { icon: <Sparkles className="w-4 h-4" />, title: t("roadmap.item10.title"), desc: t("roadmap.item10.desc"), status: "soon" },
];

export default function Roadmap() {
  const { t } = useLang();
  const roadmap = buildRoadmap(t);
  const liveItems = roadmap.filter((r) => r.status === "live");
  const soonItems = roadmap.filter((r) => r.status !== "live");
  return (
    <section
      id="roadmap"
      className="relative py-20 md:py-32 border-t border-white/5 overflow-hidden"
    >
      <div className="absolute inset-0 grid-bg opacity-30 pointer-events-none" />

      <div className="relative max-w-5xl mx-auto px-5 md:px-12">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.4 }}
          transition={{ duration: 0.6 }}
          className="max-w-2xl"
        >
          <div className="text-[10px] md:text-xs uppercase tracking-[0.22em] text-[#ccff00] font-semibold">
            {t("roadmap.eyebrow")}
          </div>
          <h2 className="font-heading text-3xl sm:text-4xl md:text-5xl font-semibold tracking-tight mt-3 md:mt-4">
            {t("roadmap.title")}
          </h2>
          <p className="text-zinc-400 mt-3 md:mt-4 text-sm md:text-base max-w-xl">
            {t("roadmap.sub")}
          </p>
        </motion.div>

        <div
          className="mt-12 md:mt-14 grid grid-cols-1 sm:grid-cols-2 gap-3 md:gap-4"
          data-testid="roadmap-grid"
        >
          {liveItems.map((item, i) => (
            <motion.div
              key={item.title}
              initial={{ opacity: 0, y: 16 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.2 }}
              transition={{ duration: 0.5, delay: i * 0.04 }}
              className="group p-4 md:p-5 rounded-xl bg-[#141416] border border-white/[0.06] hover:border-[#ccff00]/30 transition-colors"
              data-testid={`roadmap-item-${i + 1}`}
            >
              <div className="flex items-start gap-3">
                <div className="shrink-0 w-9 h-9 rounded-lg bg-[#ccff00]/10 border border-[#ccff00]/20 text-[#ccff00] flex items-center justify-center">
                  {item.icon}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-heading text-[15px] md:text-base font-medium text-white">
                      {item.title}
                    </span>
                    {item.status === "live" ? (
                      <span
                        className="inline-flex items-center gap-1 text-[9px] md:text-[10px] uppercase tracking-[0.18em] font-semibold text-[#ccff00] px-1.5 py-0.5 rounded-full border border-[#ccff00]/40 bg-[#ccff00]/10"
                        data-testid={`roadmap-status-live-${i + 1}`}
                      >
                        <span className="w-1 h-1 rounded-full bg-[#ccff00] animate-pulse" />
                        {t("roadmap.chipLive")}
                      </span>
                    ) : (
                      <span
                        className="text-[9px] md:text-[10px] uppercase tracking-[0.18em] text-zinc-500"
                        data-testid={`roadmap-status-soon-${i + 1}`}
                      >
                        {t("roadmap.chipSoon")}
                      </span>
                    )}
                  </div>
                  <p className="text-zinc-400 text-sm mt-1 leading-relaxed">
                    {item.desc}
                  </p>
                </div>
              </div>
            </motion.div>
          ))}
        </div>

        {/* Planned work stays visible but deliberately quiet — what is live
            comes first and gets the full cards. */}
        <div className="mt-8 md:mt-10" data-testid="roadmap-soon">
          <div className="text-[10px] uppercase tracking-[0.22em] text-zinc-500 font-semibold">
            {t("roadmap.chipSoon")}
          </div>
          <ul className="mt-3 flex flex-wrap gap-x-5 gap-y-1.5">
            {soonItems.map((item, i) => (
              <li
                key={item.title}
                className="text-xs md:text-[13px] text-zinc-500"
                data-testid={`roadmap-soon-${i + 1}`}
              >
                <span className="text-zinc-400">{item.title}</span>
              </li>
            ))}
          </ul>
        </div>

        <p
          className="mt-10 md:mt-12 text-[11px] md:text-xs text-zinc-500 max-w-2xl leading-relaxed"
          data-testid="roadmap-disclaimer"
        >
          {t("roadmap.footnote")}
        </p>
      </div>
    </section>
  );
}
